/**
 * Wall-clock arithmetic in a workspace time zone with `Intl` only: follow-up
 * dates are decided in local time (same hour across a DST change, Sunday moved
 * to Monday), while the database stores instants.
 */

export type ZonedDateTime = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
};

export type LocalDateTime = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second?: number;
  millisecond?: number;
};

export const DEFAULT_TIME_ZONE = "Europe/Rome";

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let value = formatters.get(timeZone);
  if (!value) {
    value = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      weekday: "short",
    });
    formatters.set(timeZone, value);
  }
  return value;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** The configured zone, or Europe/Rome when it is missing or unknown to `Intl`. */
export function safeTimeZone(timeZone: string | null | undefined): string {
  return timeZone && isValidTimeZone(timeZone) ? timeZone : DEFAULT_TIME_ZONE;
}

export function zonedParts(instant: Date, timeZone: string): ZonedDateTime {
  const parts = formatter(timeZone).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return {
    year: Number(value("year")),
    month: Number(value("month")),
    day: Number(value("day")),
    hour: Number(value("hour")) % 24,
    minute: Number(value("minute")),
    second: Number(value("second")),
    millisecond: instant.getUTCMilliseconds(),
    weekday: WEEKDAYS[value("weekday")] ?? 0,
  };
}

function offsetAt(instant: number, timeZone: string): number {
  const parts = zonedParts(new Date(instant), timeZone);
  return (
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      parts.millisecond,
    ) - instant
  );
}

/**
 * The instant of a local date and time. Out-of-range fields roll over like
 * `Date.UTC` (day 32 is the 1st of the next month); a time skipped by DST lands
 * one hour off, which never matters for a reminder.
 */
export function zonedTimeToInstant(local: LocalDateTime, timeZone: string): Date {
  const wallClock = Date.UTC(
    local.year,
    local.month - 1,
    local.day,
    local.hour,
    local.minute,
    local.second ?? 0,
    local.millisecond ?? 0,
  );
  const firstOffset = offsetAt(wallClock, timeZone);
  const secondOffset = offsetAt(wallClock - firstOffset, timeZone);
  return new Date(wallClock - secondOffset);
}

/** Day of the week of a calendar date (0 = Sunday), independent of any zone. */
export function weekdayOf(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function localOf(parts: ZonedDateTime): LocalDateTime {
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
    millisecond: parts.millisecond,
  };
}

/** Sunday is not a working day: it becomes Monday at the same wall-clock time. */
function offSunday(local: LocalDateTime): LocalDateTime {
  return weekdayOf(local.year, local.month, local.day) === 0
    ? { ...local, day: local.day + 1 }
    : local;
}

/** The same wall-clock time `days` calendar days later, moved off Sunday. */
export function addWorkingZonedDays(instant: Date, days: number, timeZone: string): Date {
  const local = localOf(zonedParts(instant, timeZone));
  return zonedTimeToInstant(offSunday({ ...local, day: local.day + days }), timeZone);
}

/** `hours` of elapsed time later, moved off Sunday (to Monday at the same wall-clock time). */
export function addWorkingHours(instant: Date, hours: number, timeZone: string): Date {
  return avoidSunday(new Date(instant.getTime() + hours * 3_600_000), timeZone);
}

/** An instant falling on Sunday in `timeZone` moves to Monday at the same wall-clock time. */
export function avoidSunday(instant: Date, timeZone: string): Date {
  const parts = zonedParts(instant, timeZone);
  return parts.weekday === 0
    ? zonedTimeToInstant({ ...localOf(parts), day: parts.day + 1 }, timeZone)
    : instant;
}

/** Midnight at the start of the local day after `instant`. */
export function startOfNextZonedDay(instant: Date, timeZone: string): Date {
  const parts = zonedParts(instant, timeZone);
  return zonedTimeToInstant(
    { year: parts.year, month: parts.month, day: parts.day + 1, hour: 0, minute: 0 },
    timeZone,
  );
}

/** Midnight at the start of the local day of `instant`. */
export function startOfZonedDay(instant: Date, timeZone: string): Date {
  const parts = zonedParts(instant, timeZone);
  return zonedTimeToInstant(
    { year: parts.year, month: parts.month, day: parts.day, hour: 0, minute: 0 },
    timeZone,
  );
}

/** `YYYY-MM-DD` of the local day of `instant`. */
export function zonedDateString(instant: Date, timeZone: string): string {
  const parts = zonedParts(instant, timeZone);
  return [
    String(parts.year).padStart(4, "0"),
    String(parts.month).padStart(2, "0"),
    String(parts.day).padStart(2, "0"),
  ].join("-");
}

/** Midnight at the start of a `YYYY-MM-DD` day in `timeZone`, Sunday moved to Monday; null if invalid. */
export function startOfZonedDate(value: string, timeZone: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const local = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  if (local.month < 1 || local.month > 12 || local.day < 1) return null;
  if (local.day > new Date(Date.UTC(local.year, local.month, 0)).getUTCDate()) return null;
  return zonedTimeToInstant(offSunday({ ...local, hour: 0, minute: 0 }), timeZone);
}

/** Parses the value of an `<input type="datetime-local">` as wall-clock time in `timeZone`. */
export function parseZonedDateTimeInput(value: string, timeZone: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  const local = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second ?? 0),
  };
  // 31 November or 15:75 would roll over to another day or hour.
  const monthDays = new Date(Date.UTC(local.year, local.month, 0)).getUTCDate();
  if (
    local.month < 1 ||
    local.month > 12 ||
    local.day < 1 ||
    local.day > monthDays ||
    local.hour > 23 ||
    local.minute > 59 ||
    local.second > 59
  ) {
    return null;
  }
  return zonedTimeToInstant(local, timeZone);
}

/** `YYYY-MM-DDTHH:mm` of an instant in `timeZone`, for an `<input type="datetime-local">`. */
export function formatZonedDateTimeInput(instant: Date, timeZone: string): string {
  const parts = zonedParts(instant, timeZone);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}
